import Card from "components/card";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import Pagination from "components/pagination/Pagination";
import Modal from "components/modal/Modal";
import EventsModal from "./EventsModal";
import EventService from "services/events.service";
import { FiSearch } from "react-icons/fi";
import Avatar from "react-avatar";
import moment from "moment";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { FaTrash } from "react-icons/fa";
import { AiFillEdit } from "react-icons/ai";
import { MdDeleteForever } from "react-icons/md";
import MapPicker from "../MapPicker";
import Select from "react-select";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const Posts = () => {
  const activateRef = useRef();
  const prevSearchTextRef = useRef("");
  const [sorting, setSorting] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(3);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [id, setId] = useState("");
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingLikeCount, setEditingLikeCount] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isUpdatingLikeCount, setIsUpdatingLikeCount] = useState(false);
  const [mediaType, setMediaType] = useState("all");
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const mediaTypeOptions = [
    { label: "All Media", value: "all" },
    { label: "Posts", value: "post" },
    { label: "Reels", value: "reel" },
  ];

  const selectStyles = {
    control: (styles, state) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1)",
      border: "1px solid rgba(255, 255, 255, 0.1) !important",
      boxShadow: "none !important",
      "&:hover": {
        border: "1px solid rgba(255, 255, 255, 0.1) !important",
      },
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#ffffff",
    }),
    option: (styles) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1) !important",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.30) !important",
        color: "#ffffff",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "12px",
      border: "1px solid #2A2A2A",
      overflow: "hidden",
      marginTop: "10px",
    }),
  };
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleOpenDelete = async (id, active, data) => {
    setSelectedUser(data);
    setId(id);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const handleDelete = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        post_id: id,
      };
      const response = await EventService.deleteFeedPost(body);
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      getPosts();
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleEditLikeCount = (rowId, currentValue) => {
    setEditingLikeCount(rowId);
    setEditingValue(currentValue.toString());
  };

  const handleSaveLikeCount = async (rowId) => {
    if (editingValue.trim() === "") {
      Toast("error", "Like count cannot be empty");
      return;
    }

    const numericValue = parseInt(editingValue, 10);
    if (isNaN(numericValue) || numericValue < 0) {
      Toast("error", "Please enter a valid non-negative number");
      return;
    }

    setIsUpdatingLikeCount(true);
    try {
      const body = {
        post_event_id: rowId,
        likecount: numericValue,
      };
      const response = await EventService.updateLikeCount(body);
      Toast("success", response.data.message);
      setEditingLikeCount(null);
      setEditingValue("");
      getPosts(); // Refresh the data
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to update like count");
    } finally {
      setIsUpdatingLikeCount(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingLikeCount(null);
    setEditingValue("");
  };

  const handleLikeCountKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow numbers
        (e.keyCode >= 48 && e.keyCode <= 57) ||
        // Allow numpad numbers
        (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Prevent any other key
    e.preventDefault();
  };

  const columns = [
    columnHelper.accessor("media", {
      id: "media",
      header: () => (
        <p className="w-[180px] text-sm font-bold uppercase text-white">
          Media
        </p>
      ),
      cell: (info) => {
        const row = info.row.original;
        const thumbnail =
          row.media?.[0] ||
          row.media_thumbnail ||
          (row.media_type === "image" ? row.media_url : row.media_thumbnail);

        return (
          <div className="flex items-center gap-3">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt="media"
                className="!h-8 !w-8 rounded-lg object-cover"
              />
            ) : (
              <Avatar
                name={row.accountDetails?.name || "Post"}
                className="!h-8 !w-8 rounded-lg object-cover"
              />
            )}
            <p className="text-sm font-semibold text-white">
              {row.accountDetails?.username || "--"}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("caption", {
      id: "caption",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white pl-3">
          Caption
        </p>
      ),
      cell: (info) => (
        <div className={`line-clamp-2 w-[200px] items-center overflow-hidden text-ellipsis whitespace-normal ml-2 ${!info.getValue() ? 'pl-10' : ''}`}>
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("post_type", {
      id: "post_type",
      header: () => (
        <p className="w-[120px] text-sm font-bold uppercase text-white">
          Post Type
        </p>
      ),
      cell: (info) => (
        <span className="capitalize text-sm text-white">
          {info.getValue() || "--"}
        </span>
      ),
    }),
    columnHelper.accessor("likecount", {
      id: "likecount",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">Likes</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const currentValue = info.getValue() || 0;
        const isEditing = editingLikeCount === rowId;

        return (
          <div className="flex w-[150px] items-center gap-2">
            {isEditing ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) => {
                    handleLikeCountKeyDown(e);
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveLikeCount(rowId);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      handleCancelEdit();
                    }
                  }}
                  className="flex h-8 w-20 items-center justify-center rounded-xl border border-white/10 bg-white/0 px-2 text-sm text-white outline-none placeholder:text-gray-600"
                  autoFocus
                  placeholder="0"
                />
                <button
                  onClick={() => handleSaveLikeCount(rowId)}
                  disabled={isUpdatingLikeCount}
                  className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
                >
                  {isUpdatingLikeCount ? (
                    <img src={Loader} alt="Loading..." className="h-4 w-4" />
                  ) : (
                    "✓"
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isUpdatingLikeCount}
                  className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <span className="text-sm text-white">{currentValue}</span>
                <AiFillEdit
                  className="cursor-pointer text-[#FF8000] hover:text-orange-600"
                  size={16}
                  onClick={() => handleEditLikeCount(rowId, currentValue)}
                />
              </>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => (
        <p className="w-[200px] text-sm font-bold uppercase text-white">
          Created At
        </p>
      ),
      cell: (info) => (
        <span className="text-sm text-white">
          {info.getValue()
            ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
            : "--"}
        </span>
      ),
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Action</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        return (
          <div className="flex ml-2 gap-3">
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 hover:border-white/80 hover:text-white/80"
              onClick={() => handleOpen(info.row.original)}
            >
              <IoEye />
            </div>
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              onClick={() =>
                handleOpenDelete(rowId, info.getValue(), info.row.original)
              }
            >
              {isViewLoading && rowId === id ? (
                <img
                  src={Loader}
                  alt="Loading..."
                  className="mx-auto h-4 w-4"
                />
              ) : (
                <MdDeleteForever size={20} />
              )}
            </div>
          </div>
        );
      },
    }),
  ];

  const handleOpen = async (item) => {
    setSelectedPost(item);
    setViewOpen(true);
    document.body.style.overflow = "hidden";
  };

  // Initialize from cache if available
  const [data, setData] = useState(tabCache.explorePosts.data || []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const getPosts = async (isSearching = false) => {
    const isFirstLoad = tabCache.explorePosts.firstLoad;
    
    // Show loader on first load, search, or pagination change
    if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
      setIsLoading(true);
    }
    
    try {
      const filters = {};
      if (mediaType !== "all") {
        filters.post_type = mediaType;
      }

      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters,
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
      };

      const response = await EventService.getAllFeedPosts(body);
      const postsData = response.data.data;
      
      // Update state and cache
      setData(postsData);
      tabCache.explorePosts.data = postsData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.explorePosts.firstLoad = false;
      }
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to fetch posts"
      );
      const emptyData = [];
      setData(emptyData);
      tabCache.explorePosts.data = emptyData;
      if (isFirstLoad) {
        tabCache.explorePosts.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false);
      setIsFilterChanging(false);
    }
  };

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.explorePosts.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getPosts(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getPosts(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText, mediaType]);

  useEffect(() => {
    setPage(1);
  }, [mediaType]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);
    setPage(newPage);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleClose = () => {
    setViewOpen(false);
    setSelectedPost(null);
    document.body.style.overflow = "unset";
  };

  return (
    <div className="mt-0.3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}

        <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
          {/* <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-white">Users</div>
          </div> */}

          <div className="mt-4 -ml-2 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center rounded-full bg-navy-900 py-3 text-white w-[180px] sm:w-[200px] xl:w-[180px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-full bg-navy-900 text-sm font-medium text-white outline-none max-w-[120px] placeholder:!text-white sm:w-fit"
                onChange={handleSearchInputChange}
                value={searchInputValue}
              />
            </div>

            <div className="default flex items-center self-end -mt-14 sm:-mt-0 sm:self-auto -mr-4">
              <Select
                className="w-[150px] !text-white"
                classNamePrefix="select"
                isSearchable={false}
                options={mediaTypeOptions}
                value={mediaTypeOptions.find(
                  (option) => option.value === mediaType
                )}
                onChange={(option) => {
                  if (option) {
                    setIsFilterChanging(true);
                    setMediaType(option.value);
                  }
                }}
                styles={selectStyles}
              />
            </div>
          </div>

          <div
            className={`table-scroll mt-4 -mx-6 relative ${
              isLoading ? "xxs:h-[40vh] xs:h-[50vh] sm:h-[60vh] overflow-hidden" : "overflow-x-auto"
            }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${isLoading ? "table-fixed h-full" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%", height: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start first:pl-6 last:pr-6"
                      >
                        <div className="items-center justify-between text-xs text-gray-200">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: "",
                            desc: "",
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className={isLoading ? "h-full" : ""}>
                {isLoading ? (
                  <tr className="md:hidden h-full">
                    <td colSpan={columns.length} className="h-full">
                      <div className="flex h-full items-center justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => {
                    return (
                      <tr
                        key={row.id}
                        className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                      >
                        
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const baseWidthClass =
                            cell.column.id === "description"
                              ? "min-w-[220px]"
                              : "min-w-[150px]";
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          return (
                            <td
                              key={cell.id}
                              className={`${baseWidthClass} border-white/0 py-3 pr-4 text-left ${isFirst ? 'pl-6' : ''} ${isLast ? 'pr-6' : ''}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {viewOpen && selectedPost && (
            <EventsModal
              isOpen={viewOpen}
              profileData={selectedPost}
              onClose={handleClose}
            />
          )}

          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
          {popupOpenDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={activateRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
                <p className="mb-6 text-gray-600">
                  Are you sure you want to delete this post?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpenDelete(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDelete();
                    }}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {confirmLoader ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Posts;
